import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin } from 'rxjs';
import { StammkundeService } from '../../../Services/stammkunde.service';
import { CustomerService } from '../../../Services/customer.service';
import { Customer } from '../../../Models/customer.model';
import {
  Auftrag,
  AuftragAssignment,
  AUFTRAG_STATUS_LABELS,
  MyStammkundeMembership,
  PlannableUser,
  StammkundeMember,
  StammkundeWorker
} from '../../../Models/stammkunde.model';
import { AuthService } from '../../../api-authorization/authorize.service';
import { EinsatzFormComponent } from '../einsatz-form/einsatz-form.component';

type BoardView = 'auftraege' | 'personal';

/**
 * Zeitliches Planungsboard eines Stammkunden:
 * links die Aufträge, rechts das Personal, in der Mitte ein 14-Tage-Raster.
 * - Auftrag auf einen Tag ziehen  -> neuer Einsatz an diesem Tag
 * - Person auf einen Einsatz ziehen -> Person wird dem Einsatz zugeteilt
 * - Person auf einen leeren Tag (Auftragszeile) -> neuer Einsatz mit dieser Person
 * Umschaltbar auf die Personal-Sicht (Zeile je Person, wie in der Ressourcenplanung).
 */
@Component({
  selector: 'app-einsatz-planung',
  templateUrl: './einsatz-planung.component.html',
  styleUrls: ['./einsatz-planung.component.css', '../stammkunden-theme.css']
})
export class EinsatzPlanungComponent implements OnInit {
  customerId!: number;
  customer: Customer | null = null;
  /** Alle Aufträge (auch abgerechnete) – für Chip-Zuordnung und Bearbeiten-Dialog. */
  alleAuftraege: Auftrag[] = [];
  /** Nur die aktiven Aufträge (Anzeige links + Zeilen der Auftrags-Sicht). */
  auftraege: Auftrag[] = [];
  /** Login-Team-Mitglieder dieses Stammkunden (Bauleiter/Vorarbeiter). */
  members: StammkundeMember[] = [];
  /** Für den Einsatz-Dialog: Team-Mitglieder als auswählbare Benutzer. */
  users: PlannableUser[] = [];
  /** Arbeiter ohne Login im Team dieses Stammkunden. */
  workers: StammkundeWorker[] = [];
  assignments: AuftragAssignment[] = [];
  myMemberships: MyStammkundeMembership[] = [];

  view: BoardView = 'auftraege';
  loading = true;

  /** Sichtbares Zeitfenster: 1 Woche (breite Spalten) oder 2 Wochen. */
  daysCount: 7 | 14 = 7;

  /** Seitenleisten einklappbar, um dem Raster mehr Platz zu geben. */
  leftCollapsed = false;
  rightCollapsed = false;

  /** Erster Tag des sichtbaren Fensters (Montag). */
  startDate = EinsatzPlanungComponent.mondayOf(new Date());
  days: Date[] = [];

  // Aktueller Drag-Zustand (HTML5 Drag & Drop)
  draggingAuftrag: Auftrag | null = null;
  draggingUser: PlannableUser | null = null;
  draggingWorker: StammkundeWorker | null = null;
  /** Einsatz-Chip wird verschoben (auf einen anderen Tag / eine andere Person). */
  draggingAssignment: AuftragAssignment | null = null;
  /** Einsatz wird über den Griff verlängert/verkürzt (Ende auf Ziel-Tag). */
  resizingAssignment: AuftragAssignment | null = null;
  hoverKey: string | null = null;

  readonly statusLabels = AUFTRAG_STATUS_LABELS;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private stammkundeService: StammkundeService,
    private customerService: CustomerService,
    private authService: AuthService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.customerId = Number(params.get('id'));
      this.buildDays();
      this.load();
    });
  }

  /** Planen darf der Bauleiter DIESES Stammkunden oder Admin/SuperAdmin. */
  get canManage(): boolean {
    if (this.authService.isSuperAdmin() || this.authService.isAdmin()) return true;
    return this.myMemberships.some(m => m.customerId === this.customerId && m.role === 'Bauleiter');
  }

  // ── Laden ──

  load(): void {
    this.loading = true;
    forkJoin({
      customer: this.customerService.getCustomer(this.customerId),
      auftraege: this.stammkundeService.getAuftraege(this.customerId),
      members: this.stammkundeService.getMembers(this.customerId),
      workers: this.stammkundeService.getWorkers(this.customerId),
      permissions: this.authService.getMyPermissions()
    }).subscribe({
      next: result => {
        this.customer = result.customer;
        this.alleAuftraege = result.auftraege ?? [];
        this.auftraege = this.alleAuftraege.filter(a => a.status !== 'Billed');
        // Es wird bewusst NUR das Team dieses Stammkunden angezeigt/verplant –
        // nicht die komplette Benutzerliste.
        this.members = result.members ?? [];
        this.users = this.members.map(m => ({ id: m.userId, userName: m.userName, email: m.email }));
        this.workers = result.workers ?? [];
        this.myMemberships = result.permissions?.stammkundenMemberships ?? [];
        this.loading = false;
        this.loadAssignments();
      },
      error: () => {
        this.snackBar.open('Fehler beim Laden des Planungsboards', 'OK', { duration: 4000 });
        this.loading = false;
      }
    });
  }

  loadAssignments(): void {
    const from = this.toIso(this.days[0]);
    const to = this.toIso(this.addDays(this.days[this.days.length - 1], 1));
    this.stammkundeService.getCustomerAssignments(this.customerId, from, to).subscribe({
      next: assignments => this.assignments = assignments ?? [],
      error: () => this.snackBar.open('Einsätze konnten nicht geladen werden', 'OK', { duration: 4000 })
    });
  }

  // ── Zeitfenster ──

  private buildDays(): void {
    this.days = Array.from({ length: this.daysCount }, (_, i) => this.addDays(this.startDate, i));
  }

  setDaysCount(count: 7 | 14): void {
    if (this.daysCount === count) return;
    this.daysCount = count;
    this.buildDays();
    this.loadAssignments();
  }

  prevWeek(): void {
    this.startDate = this.addDays(this.startDate, -7);
    this.buildDays();
    this.loadAssignments();
  }

  nextWeek(): void {
    this.startDate = this.addDays(this.startDate, 7);
    this.buildDays();
    this.loadAssignments();
  }

  today(): void {
    this.startDate = EinsatzPlanungComponent.mondayOf(new Date());
    this.buildDays();
    this.loadAssignments();
  }

  isToday(day: Date): boolean {
    const now = new Date();
    return day.getFullYear() === now.getFullYear()
      && day.getMonth() === now.getMonth()
      && day.getDate() === now.getDate();
  }

  isWeekend(day: Date): boolean {
    const d = day.getDay();
    return d === 0 || d === 6;
  }

  // ── Zuordnung Einsätze -> Zellen ──

  assignmentsForAuftragDay(auftrag: Auftrag, day: Date): AuftragAssignment[] {
    return this.assignments.filter(e => e.auftragId === auftrag.id && this.overlapsDay(e, day));
  }

  assignmentsForUserDay(user: PlannableUser, day: Date): AuftragAssignment[] {
    return this.assignments.filter(e =>
      (e.assignedUserId === user.id || e.participantUserIds.includes(user.id))
      && this.overlapsDay(e, day));
  }

  assignmentsForWorkerDay(worker: StammkundeWorker, day: Date): AuftragAssignment[] {
    return this.assignments.filter(e =>
      (e.workerIds ?? []).includes(worker.id) && this.overlapsDay(e, day));
  }

  private overlapsDay(e: AuftragAssignment, day: Date): boolean {
    const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate());
    const dayEnd = this.addDays(dayStart, 1);
    const start = new Date(e.start);
    const end = new Date(e.end);
    return start < dayEnd && end > dayStart;
  }

  personCount(e: AuftragAssignment): number {
    return 1 + (e.participantUserIds?.length ?? 0) + (e.workerIds?.length ?? 0);
  }

  // ── Drag & Drop ──

  startDragAuftrag(auftrag: Auftrag, event: DragEvent): void {
    if (!this.canManage) { event.preventDefault(); return; }
    this.draggingAuftrag = auftrag;
    event.dataTransfer?.setData('text/plain', `auftrag:${auftrag.id}`);
  }

  startDragUser(user: PlannableUser, event: DragEvent): void {
    if (!this.canManage) { event.preventDefault(); return; }
    this.draggingUser = user;
    event.dataTransfer?.setData('text/plain', `user:${user.id}`);
  }

  startDragWorker(worker: StammkundeWorker, event: DragEvent): void {
    if (!this.canManage) { event.preventDefault(); return; }
    this.draggingWorker = worker;
    event.dataTransfer?.setData('text/plain', `worker:${worker.id}`);
  }

  startDragAssignment(e: AuftragAssignment, event: DragEvent): void {
    if (!this.canManage) { event.preventDefault(); return; }
    this.draggingAssignment = e;
    event.dataTransfer?.setData('text/plain', `einsatz:${e.id}`);
    event.stopPropagation();
  }

  startResizeAssignment(e: AuftragAssignment, event: DragEvent): void {
    if (!this.canManage) { event.preventDefault(); return; }
    this.resizingAssignment = e;
    event.dataTransfer?.setData('text/plain', `resize:${e.id}`);
    event.stopPropagation();
  }

  endDrag(): void {
    this.draggingAuftrag = null;
    this.draggingUser = null;
    this.draggingWorker = null;
    this.draggingAssignment = null;
    this.resizingAssignment = null;
    this.hoverKey = null;
  }

  allowDrop(event: DragEvent, key: string): void {
    if (!this.draggingAuftrag && !this.draggingUser && !this.draggingWorker
      && !this.draggingAssignment && !this.resizingAssignment) return;
    event.preventDefault();
    this.hoverKey = key;
  }

  /**
   * Dragover auf einem Einsatz-Chip: nur für Personen-/Arbeiter-Drags zuständig (Chip
   * hervorheben, nicht die Zelle). Auftrags-Drags fallen zur Tageszelle durch.
   */
  allowDropOnChip(event: DragEvent, key: string): void {
    if (!this.draggingUser && !this.draggingWorker) return;
    event.preventDefault();
    event.stopPropagation();
    this.hoverKey = key;
  }

  clearHover(key: string): void {
    if (this.hoverKey === key) this.hoverKey = null;
  }

  /** Drop auf eine Tageszelle in der Auftrags-Sicht. */
  dropOnAuftragDay(auftrag: Auftrag, day: Date, event: DragEvent): void {
    event.preventDefault();
    if (this.draggingAuftrag) {
      // Gezogener Auftrag zählt – unabhängig von der Zeile, auf der er landet.
      this.openCreateDialog(this.draggingAuftrag, day);
    } else if (this.draggingUser) {
      this.openCreateDialog(auftrag, day, this.draggingUser.id);
    } else if (this.draggingWorker) {
      this.openCreateDialog(auftrag, day, undefined, this.draggingWorker.id);
    } else if (this.draggingAssignment) {
      this.moveAssignment(this.draggingAssignment, day);
    } else if (this.resizingAssignment) {
      this.resizeAssignment(this.resizingAssignment, day);
    }
    this.endDrag();
  }

  /** Drop auf eine Tageszelle in der Personal-Sicht (Login-Mitglied). */
  dropOnUserDay(user: PlannableUser, day: Date, event: DragEvent): void {
    event.preventDefault();
    if (this.draggingAuftrag) {
      this.openCreateDialog(this.draggingAuftrag, day, user.id);
    } else if (this.draggingAssignment) {
      // Verschieben auf eine andere Personen-Zeile wechselt den Verantwortlichen mit.
      this.moveAssignment(this.draggingAssignment, day, user);
    } else if (this.resizingAssignment) {
      this.resizeAssignment(this.resizingAssignment, day);
    }
    this.endDrag();
  }

  /** Drop auf eine Tageszelle in der Personal-Sicht (Arbeiter-Zeile). */
  dropOnWorkerDay(worker: StammkundeWorker, day: Date, event: DragEvent): void {
    event.preventDefault();
    if (this.draggingAuftrag) {
      this.openCreateDialog(this.draggingAuftrag, day, undefined, worker.id);
    } else if (this.draggingAssignment) {
      // Arbeiter können nicht Verantwortliche sein -> nur der Tag wird verschoben.
      this.moveAssignment(this.draggingAssignment, day);
    } else if (this.resizingAssignment) {
      this.resizeAssignment(this.resizingAssignment, day);
    }
    this.endDrag();
  }

  /** Drop einer Person/eines Arbeiters auf einen Einsatz-Chip (Aufträge fallen zur Zelle durch). */
  dropOnAssignment(e: AuftragAssignment, event: DragEvent): void {
    if (!this.draggingUser && !this.draggingWorker) return; // Auftrags-Drop übernimmt die Tageszelle.
    event.preventDefault();
    event.stopPropagation();
    const user = this.draggingUser;
    const worker = this.draggingWorker;
    this.endDrag();
    if (e.auftragId == null) return;

    if (user) {
      if (e.assignedUserId === user.id || e.participantUserIds.includes(user.id)) {
        this.snackBar.open('Diese Person ist dem Einsatz bereits zugeteilt.', 'OK', { duration: 3000 });
        return;
      }
      this.stammkundeService.addParticipant(e.auftragId, e.id, user.id).subscribe({
        next: updated => {
          this.assignments = this.assignments.map(x => x.id === updated.id ? updated : x);
          this.snackBar.open(`${user.userName || user.email || 'Person'} zugeteilt`, 'OK', { duration: 3000 });
        },
        error: () => this.snackBar.open('Zuteilen fehlgeschlagen', 'OK', { duration: 4000 })
      });
    } else if (worker) {
      if ((e.workerIds ?? []).includes(worker.id)) {
        this.snackBar.open('Dieser Arbeiter ist dem Einsatz bereits zugeteilt.', 'OK', { duration: 3000 });
        return;
      }
      this.stammkundeService.addWorkerToAssignment(e.auftragId, e.id, worker.id).subscribe({
        next: updated => {
          this.assignments = this.assignments.map(x => x.id === updated.id ? updated : x);
          this.snackBar.open(`${worker.name} zugeteilt`, 'OK', { duration: 3000 });
        },
        error: () => this.snackBar.open('Zuteilen fehlgeschlagen', 'OK', { duration: 4000 })
      });
    }
  }

  // ── Einsatz verschieben / verlängern ──

  /**
   * Verschiebt einen Einsatz auf einen anderen Tag (Uhrzeiten und Dauer bleiben).
   * Optional wechselt in der Personal-Sicht der Verantwortliche mit.
   */
  private moveAssignment(e: AuftragAssignment, day: Date, newResponsible?: PlannableUser): void {
    if (e.auftragId == null) return;

    const start = new Date(e.start);
    const end = new Date(e.end);
    const duration = end.getTime() - start.getTime();
    const newStart = new Date(day.getFullYear(), day.getMonth(), day.getDate(),
      start.getHours(), start.getMinutes());
    const newEnd = new Date(newStart.getTime() + duration);

    const payload = this.buildPayloadFrom(e);
    payload.start = this.toLocalDateTime(newStart);
    payload.end = this.toLocalDateTime(newEnd);

    let reassigned = false;
    if (newResponsible && newResponsible.id !== e.assignedUserId) {
      payload.assignedUserId = newResponsible.id;
      reassigned = true;
    }

    const unchanged = !reassigned && newStart.getTime() === start.getTime();
    if (unchanged) return;

    this.stammkundeService.updateAssignment(e.auftragId, e.id, payload).subscribe({
      next: updated => {
        this.assignments = this.assignments.map(x => x.id === updated.id ? updated : x);
        const who = reassigned ? ` – Verantwortlich: ${this.userLabel(newResponsible!)}` : '';
        const invite = e.inviteSent ? ' (Einladung ggf. neu senden)' : '';
        this.snackBar.open(`Einsatz verschoben${who}${invite}`, 'OK', { duration: 4000 });
      },
      error: err => {
        const message = (typeof err?.error === 'string' && err.error) || err?.error?.message
          || 'Verschieben fehlgeschlagen.';
        this.snackBar.open(message, 'OK', { duration: 5000 });
      }
    });
  }

  /** Setzt das Ende eines Einsatzes auf den Ziel-Tag (Endzeit bleibt) – verlängern/verkürzen. */
  private resizeAssignment(e: AuftragAssignment, day: Date): void {
    if (e.auftragId == null) return;

    const start = new Date(e.start);
    const end = new Date(e.end);
    const newEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate(),
      end.getHours(), end.getMinutes());

    if (newEnd <= start) {
      this.snackBar.open('Das Ende muss nach dem Beginn liegen.', 'OK', { duration: 4000 });
      return;
    }
    if (newEnd.getTime() === end.getTime()) return;

    const payload = this.buildPayloadFrom(e);
    payload.end = this.toLocalDateTime(newEnd);

    const verb = newEnd > end ? 'verlängert' : 'verkürzt';
    this.stammkundeService.updateAssignment(e.auftragId, e.id, payload).subscribe({
      next: updated => {
        this.assignments = this.assignments.map(x => x.id === updated.id ? updated : x);
        this.snackBar.open(`Einsatz ${verb} bis ${newEnd.toLocaleDateString('de-DE')}`, 'OK', { duration: 4000 });
      },
      error: err => {
        const message = (typeof err?.error === 'string' && err.error) || err?.error?.message
          || 'Ändern fehlgeschlagen.';
        this.snackBar.open(message, 'OK', { duration: 5000 });
      }
    });
  }

  /** Vollständiger Update-Payload aus einem bestehenden Einsatz (Kalender wird nicht neu versendet). */
  private buildPayloadFrom(e: AuftragAssignment) {
    return {
      assignedUserId: e.assignedUserId,
      participantUserIds: [...e.participantUserIds],
      workerIds: [...(e.workerIds ?? [])],
      title: e.title,
      start: e.start,
      end: e.end,
      location: e.location ?? undefined,
      notes: e.notes ?? undefined,
      workStatus: e.workStatus,
      sendInvite: false
    };
  }

  private toLocalDateTime(d: Date): string {
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    const hh = `${d.getHours()}`.padStart(2, '0');
    const mm = `${d.getMinutes()}`.padStart(2, '0');
    return `${y}-${m}-${day}T${hh}:${mm}:00`;
  }

  // ── Dialoge ──

  /** Klick auf eine leere Tageszelle in der Auftrags-Sicht legt direkt einen Einsatz an. */
  cellClickCreate(auftrag: Auftrag, day: Date): void {
    if (!this.canManage) return;
    this.openCreateDialog(auftrag, day);
  }

  /** "Einsatz planen"-Menü im Kopf: Auftrag wählen, Dialog öffnet mit heutigem Datum. */
  createForAuftrag(auftrag: Auftrag): void {
    this.openCreateDialog(auftrag, new Date());
  }

  private openCreateDialog(auftrag: Auftrag, day: Date, prefillUserId?: string, prefillWorkerId?: number): void {
    if (!this.canManage) return;
    const dialogRef = this.dialog.open(EinsatzFormComponent, {
      width: '640px',
      maxWidth: '96vw',
      data: {
        auftrag,
        users: this.users,
        workers: this.workers,
        prefillDate: this.toIso(day),
        prefillUserId,
        prefillWorkerId
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadAssignments();
        this.snackBar.open('Einsatz geplant', 'OK', { duration: 3000 });
      }
    });
  }

  editAssignment(e: AuftragAssignment): void {
    if (!this.canManage || e.auftragId == null) return;
    const auftrag = this.alleAuftraege.find(a => a.id === e.auftragId);
    if (!auftrag) return;
    const dialogRef = this.dialog.open(EinsatzFormComponent, {
      width: '640px',
      maxWidth: '96vw',
      data: { auftrag, assignment: e, users: this.users, workers: this.workers }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadAssignments();
        this.snackBar.open('Einsatz aktualisiert', 'OK', { duration: 3000 });
      }
    });
  }

  // ── Anzeige-Helfer ──

  userLabel(user: PlannableUser): string {
    return user.userName || user.email || user.id;
  }

  shortName(name?: string | null): string {
    if (!name) return '?';
    return name.length > 14 ? name.slice(0, 13) + '…' : name;
  }

  plannedDays(auftrag: Auftrag): number {
    return this.assignments.filter(e => e.auftragId === auftrag.id).length;
  }

  userAssignmentCount(user: PlannableUser): number {
    return this.assignments.filter(e =>
      e.assignedUserId === user.id || e.participantUserIds.includes(user.id)).length;
  }

  workerAssignmentCount(worker: StammkundeWorker): number {
    return this.assignments.filter(e => (e.workerIds ?? []).includes(worker.id)).length;
  }

  memberRole(user: PlannableUser): string | null {
    return this.members.find(m => m.userId === user.id)?.role ?? null;
  }

  trackByWorker(_: number, w: StammkundeWorker): number { return w.id; }

  /** Deutsche Einzahl/Mehrzahl für Einsatz-Zähler. */
  einsatzWort(n: number): string {
    return n === 1 ? 'Einsatz' : 'Einsätze';
  }

  /**
   * Crew eines Einsatzes für die Chip-Anzeige: Beteiligte (Login) und Arbeiter,
   * maximal 3 Zeilen – der Rest wird als "+n" zusammengefasst.
   */
  chipCrew(e: AuftragAssignment): { name: string; icon: string }[] {
    const all = [
      ...e.participantNames.map(n => ({ name: n, icon: 'group' })),
      ...(e.workerNames ?? []).map(n => ({ name: n, icon: 'construction' }))
    ];
    return all.slice(0, 3);
  }

  chipCrewMore(e: AuftragAssignment): number {
    const total = e.participantNames.length + (e.workerNames?.length ?? 0);
    return Math.max(0, total - 3);
  }

  openAuftrag(auftrag: Auftrag): void {
    this.router.navigate(['/stammkunden', this.customerId, 'auftrag', auftrag.id]);
  }

  back(): void {
    this.router.navigate(['/stammkunden', this.customerId]);
  }

  trackByAuftrag(_: number, a: Auftrag): number { return a.id; }
  trackByUser(_: number, u: PlannableUser): string { return u.id; }
  trackByDay(_: number, d: Date): number { return d.getTime(); }

  // ── Datums-Helfer (lokale Wandzeit, keine UTC-Verschiebung) ──

  private toIso(date: Date): string {
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, '0');
    const d = `${date.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private addDays(date: Date, days: number): Date {
    const copy = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    copy.setDate(copy.getDate() + days);
    return copy;
  }

  private static mondayOf(date: Date): Date {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const weekday = (d.getDay() + 6) % 7; // Mo=0 ... So=6
    d.setDate(d.getDate() - weekday);
    return d;
  }
}
